import React, { useState } from "react";
import { PieChart } from "react-minimal-pie-chart";
import styled from "styled-components";
import { getColor } from "../../constants/colors";
import { useTranslation } from "react-i18next";
import theme from "../../theme/feedback";

const Panel = styled.div`
  background: ${theme.color.surface};
  box-shadow: 0px 2px 8px rgba(21, 34, 50, 0.08);
  border: 1px solid ${theme.color.border};
  border-radius: ${theme.radius.lg};
  padding: ${theme.spacing.lg};
  margin: ${theme.spacing.lg} 0 ${theme.spacing.sm} 0;
`;

const PanelTitle = styled.h3`
  font-family: ${theme.font.family};
  font-size: ${theme.font.sizeLg};
  font-weight: ${theme.font.weightSemibold};
  color: ${theme.color.text};
  margin: 0 0 ${theme.spacing.md} 0;
  padding: 0;
`;

const ChartsRow = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${theme.spacing.lg};

  @media only screen and (max-width: 991px) and (orientation: portrait) {
    flex-direction: column;
  }
`;

const ChartCell = styled.div`
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: center;

  &:first-child {
    border-right: 1px solid ${theme.color.border};
  }

  @media only screen and (max-width: 991px) and (orientation: portrait) {
    &:first-child {
      border-right: none;
      border-bottom: 1px solid ${theme.color.border};
      padding-bottom: ${theme.spacing.md};
    }
  }
`;

const ChartCaption = styled.span`
  font-family: ${theme.font.family};
  font-size: ${theme.font.sizeSm};
  font-weight: ${theme.font.weightSemibold};
  letter-spacing: 0.6px;
  text-transform: uppercase;
  color: ${theme.color.textMuted};
  margin-bottom: ${theme.spacing.md};
`;

const DonutWrap = styled.div`
  position: relative;
  width: 150px;
  height: 150px;
`;

const DonutCenter = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  pointer-events: none;
`;

const CenterValue = styled.span`
  font-family: ${theme.font.family};
  font-size: 26px;
  font-weight: ${theme.font.weightBold};
  line-height: 1;
  color: ${theme.color.text};
`;

const CenterLabel = styled.span`
  font-family: ${theme.font.family};
  font-size: 11px;
  color: ${theme.color.textMuted};
  margin-top: 2px;
`;

const Legend = styled.ul`
  list-style: none;
  padding: 0;
  margin: ${theme.spacing.md} 0 0 0;
  width: 100%;
  max-width: 220px;
`;

const LegendItem = styled.li`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  font-family: ${theme.font.family};
  font-size: ${theme.font.sizeMd};
  color: ${theme.color.text};
  padding: 4px 0;
  cursor: pointer;
  opacity: ${(props) => (props.dimmed ? 0.45 : 1)};
  transition: opacity 0.2s ease;
`;

const Swatch = styled.span`
  width: 11px;
  height: 11px;
  border-radius: 3px;
  background: ${(props) => props.color};
  flex-shrink: 0;
`;

const LegendLabel = styled.span`
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-transform: capitalize;
`;

const LegendValue = styled.span`
  font-weight: ${theme.font.weightSemibold};
  color: ${theme.color.text};
`;

const LegendPercent = styled.span`
  font-size: ${theme.font.sizeSm};
  color: ${theme.color.textMuted};
  min-width: 38px;
  text-align: right;
`;

const Empty = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 150px;
  color: ${theme.color.textMuted};
  font-family: ${theme.font.family};
  font-size: ${theme.font.sizeMd};
`;

const Donut = ({ caption, data, emptyText }) => {
  const [selected, setSelected] = useState(undefined);
  const total = data.reduce((sum, d) => sum + d.value, 0);

  return (
    <ChartCell>
      <ChartCaption>{caption}</ChartCaption>
      {total === 0 ? (
        <Empty>{emptyText}</Empty>
      ) : (
        <>
          <DonutWrap>
            <PieChart
              data={data}
              lineWidth={26}
              startAngle={-90}
              paddingAngle={data.length > 1 ? 2 : 0}
              rounded={data.length > 1}
              radius={48}
              segmentsStyle={{ transition: "stroke .3s, opacity .3s", cursor: "pointer" }}
              segmentsShift={(index) => (index === selected ? 4 : 0)}
              onClick={(_, index) =>
                setSelected(index === selected ? undefined : index)
              }
            />
            <DonutCenter>
              <CenterValue>{total}</CenterValue>
              <CenterLabel>{caption}</CenterLabel>
            </DonutCenter>
          </DonutWrap>
          <Legend>
            {data.map((entry, idx) => (
              <LegendItem
                key={idx}
                dimmed={selected !== undefined && selected !== idx}
                onClick={() =>
                  setSelected(idx === selected ? undefined : idx)
                }
              >
                <Swatch color={entry.color} />
                <LegendLabel>{entry.title}</LegendLabel>
                <LegendValue>{entry.value}</LegendValue>
                <LegendPercent>
                  {Math.round((entry.value / total) * 100)}%
                </LegendPercent>
              </LegendItem>
            ))}
          </Legend>
        </>
      )}
    </ChartCell>
  );
};

export const DashboardCharts = ({ title = "", dashboard_data }) => {
  const { t } = useTranslation();

  const byHand = dashboard_data.charts.byHand.map((handValue, idx) => ({
    title: t(handValue.hand),
    value: handValue.total * 1,
    color: getColor(dashboard_data.charts.byHand.length, idx),
  }));

  const byStyle = dashboard_data.charts.byStyle.map((styleValue, idx) => ({
    title: t(styleValue.style === "" ? "noSelected" : styleValue.style),
    value: styleValue.total * 1,
    color: getColor(dashboard_data.charts.byStyle.length, idx),
  }));

  return (
    <Panel>
      <PanelTitle>{title}</PanelTitle>
      <ChartsRow>
        <Donut
          caption={t("general.hand", { defaultValue: "Hand" })}
          data={byHand}
          emptyText={t("message.info.no_data", { defaultValue: "No data" })}
        />
        <Donut
          caption={t("general.style", { defaultValue: "Style" })}
          data={byStyle}
          emptyText={t("message.info.no_data", { defaultValue: "No data" })}
        />
      </ChartsRow>
    </Panel>
  );
};
